// -----------------------------------------------------------------------------
// src/routes/CreatorConnectRoute.jsx — REDESIGNED: Header only on mobile
// Now mirrors the normal user layout pattern (desktop = no header, mobile = header)
// -----------------------------------------------------------------------------
import React, { useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import CreatorHeader from './CreatorHeader';
import { Box,
  CssBaseline,
  IconButton, Toolbar, } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { lightTheme } from '../Utils/baseTheme';
import CreatorNavbar from './CreatorNavbar';
import useIsDesktop from '../Hooks/useIsDesktop';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowLeftSharpIcon from '@mui/icons-material/KeyboardArrowLeftSharp';
import KeyboardArrowRightSharpIcon from '@mui/icons-material/KeyboardArrowRightSharp';
import { drawerWidth } from '../Utils/constants';

/* ------------------------------------------------------------------ */
/* Drawer‐aware content sizing - Updated for no header on desktop */
/* ------------------------------------------------------------------ */
const openedMixin = (theme) => ({
  width: { xs: '100%', md: `calc(100% - ${drawerWidth})` },
  ml: { xs: 0, md: drawerWidth }, // margin‐left on desktop
  transition: theme.transitions.create(['width', 'margin-left'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
});

const closedMixin = (theme) => ({
  width: { xs: '100%', md: `calc(100% - 80px)` }, // 80px = collapsed drawer width
  ml: { xs: 0, md: '80px' },
  transition: theme.transitions.create(['width', 'margin-left'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
});

const CreatorConnectRoute = () => {
  const isDesktop = useIsDesktop();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(isDesktop); 
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileActions, setMobileActions] = useState([]);
  const isPartnershipDetailRoute = useMemo(
    () =>
      /^\/partnerships\/[^/]+\/?$/.test(location.pathname) ||
      /^\/creatorconnect\/partnerships\/[^/]+\/?$/.test(location.pathname) ||
      /^\/agency\/creators\/[^/]+\/collaborations\/partnerships\/[^/]+\/?$/.test(location.pathname),
    [location.pathname]
  );
  const hideMobileShell = !isDesktop && isPartnershipDetailRoute;
  
  const isExtended = !isDesktop || isOpen;

  const handleUnreadCountChange = (count) => setUnreadCount(count);
  const handleGetMobileActions = (actions) => setMobileActions(actions);

  /* ---------- menu button - no longer needed on desktop since navbar handles it ---------- */
  const MenuButton = !isDesktop ? (
    <IconButton
      edge="start"
      aria-label="menu"
      sx={{ mr: 2 }}
      onClick={() => setIsOpen((prev) => !prev)}
    >
      <MenuIcon sx={{ color: '#fff' }} />
    </IconButton>
  ) : null;

  return (
    <ThemeProvider theme={lightTheme}>
      {!hideMobileShell && (
        <CreatorNavbar
          isDesktop={isDesktop}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onToggleExtend={() => setIsOpen(prev => !prev)}
          onUnreadCountChange={handleUnreadCountChange}
          onGetMobileActions={handleGetMobileActions}
        />
      )}
      
      <CssBaseline />
      
      {/* Show header ONLY on mobile - mirroring BlitzLayout pattern */}
      {!hideMobileShell && !isDesktop && (
        <>
          <CreatorHeader
            menuIcon={MenuButton}
            isDesktop={isDesktop}
            mobileActions={mobileActions}
          />
          {/* Reserve header space on mobile */}
          <Toolbar />
        </>
      )}

      {/* Page container reacts to drawer size - no header offset on desktop */}
      <Box
        id="TopPageContainer"
        sx={(theme) => (isExtended ? openedMixin(theme) : closedMixin(theme))}
      >
        <Outlet />
      </Box>
    </ThemeProvider>
  );
};

export default CreatorConnectRoute;
